import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  AlertController,
  LoadingController,
  ModalController
} from '@ionic/angular';
import { List } from 'immutable';
import { Subject } from 'rxjs';
import { debounceTime, takeLast } from 'rxjs/operators';
import { Client, Stylist } from 'src/app/models';
import { ClientService, StylistService, UserService } from 'src/app/services';
import { Utilities } from 'src/app/utilities/utilities';

@Component({
  selector: 'smr-client-picker',
  templateUrl: './smr-client-picker.component.html',
  styleUrls: ['./smr-client-picker.component.scss']
})
export class ClientPickerComponent
  implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
  @ViewChild('search') searchBar: any;
  @Input() title: string = "";
  @Input() stylist: Stylist;
  @Input() ignoreClient: Client;

  // @Input() infiniteScroll: Observable<InfiniteScroll>;

  @Output() clientSelect = new EventEmitter<Client>();

  @Output() clientListLoad = new EventEmitter<List<Client>>();

  public refreshSelectedClient = new Subject<Client>();


  public isSearchingForClients: boolean;

  public errorOccurredWhileSearchingForClients: boolean;

  @Input() searchTerm: string = "";

  public clientSearchResult: List<Client>;

  public isLoadingClientList: boolean;

  public errorOccurredWhileLoadingClientList: boolean;

  public page: number;

  public clientList: List<Client>;

  public searchField: UntypedFormControl;

  public searchForm: UntypedFormGroup;

  public selectedClient: Client;

  public clientListFullyLoaded: boolean;

  public firstLoad = true;

  constructor(
    private alertCtrl: AlertController,
    private clientService: ClientService,
    private stylistService: StylistService,
    private loadingCtrl: LoadingController,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private modalController: ModalController,
  ) {
    this.isSearchingForClients = false;
    this.errorOccurredWhileSearchingForClients = false;
    this.clientList = List([]);
    this.clientSearchResult = List([]);
    this.stylist = this.userService.getUser();
    this.page = 1;

    this.searchField = new UntypedFormControl();
    this.searchForm = this.formBuilder.group({
      searchField: this.searchField
    });
    this.clientService.editClientList$.subscribe((client: Client) => {
      this.editClient(client); 
    });
  }

  public ngOnInit() {

    this.stylist = this.stylist ? this.stylist : this.userService.getUser();
    // this.loadClients();
    // removed because when changing the stylist above, it was already being called by ngOnChange, as the stylist is an Input variable.

    this.refreshSelectedClient.subscribe((client: Client) => {
      if (client) {
        // update the selected client
        const clientSearchIndex: number = this.clientSearchResult.findIndex(
          (c) => c.getID() === client.getID()
        );
        if (clientSearchIndex >= 0) {
          this.clientSearchResult = this.clientSearchResult.set(
            clientSearchIndex,
            client
          );
        }

        const clientListIndex: number = this.clientList.findIndex(
          (c) => c.getID() === client.getID()
        );
        if (clientListIndex >= 0) {
          this.clientList = this.clientList.set(clientListIndex, client);
        }

        this.changeDetectorRef.markForCheck();
      } else {
        // remove the selected client
        const clientSearchIndex: number = this.clientSearchResult.findIndex(
          (c) => c.getID() === this.selectedClient.getID()
        );
        if (clientSearchIndex >= 0) {
          this.clientSearchResult = this.clientSearchResult.remove(
            clientSearchIndex
          );
        }

        const clientListIndex: number = this.clientList.findIndex(
          (c) => c.getID() === this.selectedClient.getID()
        );
        if (clientListIndex >= 0) {
          this.clientList = this.clientList.remove(clientListIndex);
        }

        this.selectedClient = undefined;
        this.changeDetectorRef.markForCheck();

        this.clientSelect.emit(this.selectedClient);
      }
    });

    // source: https://angular-2-training-book.rangle.io/handout/http/search_with_switchmap.html
    this.searchField.valueChanges.pipe(debounceTime(400)).subscribe({
      next: (term: string) => {
        this.isSearchingForClients = true;
        this.errorOccurredWhileSearchingForClients = false;
        this.changeDetectorRef.markForCheck();

        let searchClientsForStylist: Stylist = this.stylist;
        let useStylistSettings: string;

        if (searchClientsForStylist.getID() === 0) {
          searchClientsForStylist = this.userService.getUser();
          useStylistSettings = 'true';
        }

        this.clientService
          .searchClients(term, searchClientsForStylist, useStylistSettings)
          .subscribe({
            next: (searchResult: List<Client>) => {
              this.clientSearchResult = searchResult;

              if (this.ignoreClient) {
                this.clientSearchResult = this.clientSearchResult.filter((client) =>
                  client.id !== this.ignoreClient.id
                ).toList();
              }
              this.isSearchingForClients = false;
              this.changeDetectorRef.markForCheck();
            },
            error: (error) => {
              this.isSearchingForClients = false;
              this.errorOccurredWhileSearchingForClients = true;
              this.changeDetectorRef.markForCheck();
            }
          });
      },
      error: (error) => {
        this.isSearchingForClients = false;
        this.errorOccurredWhileSearchingForClients = true;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  public ngOnDestroy() { }

  public ngAfterViewChecked() {
    if (this.firstLoad) {
      setTimeout(() => {
        this.searchBar.setFocus();
      }, 1000);

      this.firstLoad = false;
    }
  }

  public ngOnChanges(changes) {
    if (changes && changes['stylist']) {
      this.searchTerm = '';
      this.isSearchingForClients = false;
      this.errorOccurredWhileSearchingForClients = false;
      this.page = 1;
      this.clientList = List([]);
      this.loadClients();
    }
  }

  public onSearch($event) {
    const value: string = $event.target.value;
    if (this.clientService.isTermSearchable(value)) {
      this.isSearchingForClients = true;
    } else {
      this.isSearchingForClients = false;
    }
  }

  public async onClientSelect(
    client: Client,
    addIntoSearchHistory: boolean,
    clientIndex: number
  ) {
    const loader = await this.loadingCtrl.create({
      spinner: 'crescent'
    });
    loader.present();
    this.clientService.loadClientInfo(client.id).subscribe({
      next: (_client: Client) => {
        loader.dismiss().then(() => {
          this.clientSelect.emit(_client);
          this.selectedClient = _client;
          if (this.ignoreClient)
            this.modalController.dismiss(client)
        });
      },
      error: (err) => {
        loader.dismiss().then(() => {
          this.alertCtrl
            .create(Utilities.getDefaultErrorMessageConfig())
            .then((alert) => alert.present());
        });
      }
    });
  }

  public loadClients(infiniteScroll = null) {
    if (!infiniteScroll) {
      this.isLoadingClientList = true;
      this.errorOccurredWhileLoadingClientList = false;
    }

    this.stylistService
      .getClients(this.stylist, this.page)
      .pipe(takeLast(1))
      .subscribe({
        next: (clients) => {
          this.page = this.page + 1;
          this.clientList = this.clientList.push(...clients.toJSON());

          this.clientListLoad.next(this.clientList);

          this.clientListFullyLoaded = clients.count() < 30;
          this.changeDetectorRef.markForCheck();

          if (infiniteScroll) {
            infiniteScroll.target.complete();
          } else {
            this.isLoadingClientList = false;
          }


        },
        error: (err) => {
          if (infiniteScroll) {
            infiniteScroll.target.complete();
          } else {
            this.isLoadingClientList = false;
            this.errorOccurredWhileLoadingClientList = true;
          }

          this.changeDetectorRef.markForCheck();
        }
      });


    if (this.ignoreClient) {
      this.clientList = this.clientList.filter((client) =>
        client.id !== this.ignoreClient.id
      ).toList();
    }

  }
  public onInfiniteScroll($event) {
    this.loadClients($event);
  }

  public addClient(client: Client) {
    const newList = this._sortClientListAlphabetically([...this.clientList.toJSON(), client]);

    this.clientList = List([]);
    this.clientList = List(newList);

    this.selectedClient = client;
    this.changeDetectorRef.detectChanges();
  }
  public editClient(client: Client) {
    const index = this.clientList.findIndex(c => c.id === client.id);
    if (index !== -1) {
      const updatedList = this.clientList.set(index, client);
      const sortedList = List(this._sortClientListAlphabetically(updatedList.toJSON()));
      this.clientList = sortedList;
      this.changeDetectorRef.detectChanges();
    }
  }

  public deleteClient(client: Client) {
    const newList = this.clientList.toJSON().filter((c) => c.id !== client.id);

    this.clientList = List([]);
    this.clientList = List(newList);

    this.selectedClient = undefined;

    this.changeDetectorRef.markForCheck();
  }

  public onDismiss() {
    this.modalController.dismiss()
  }

  private _sortClientListAlphabetically(clientList: Array<Client>): Array<Client> {
    return clientList.sort((a, b) => {
      const nameA = a.firstName.toLowerCase();
      const nameB = b.firstName.toLowerCase();

      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    });
  }
}
